import { AlertTriangle, Bell, BellOff, CheckCircle, Monitor } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { notificationManager } from "@/domains/notifications/services";

interface BrowserNotificationSettingsProps {
  className?: string;
}

export function BrowserNotificationSettings({ className }: BrowserNotificationSettingsProps) {
  const { t } = useTranslation("settings");
  const [browserPermission, setBrowserPermission] = useState<
    NotificationPermission | "unsupported"
  >("default");
  const [isRequesting, setIsRequesting] = useState(false);

  // Update permission status
  const updatePermission = useCallback(() => {
    try {
      const permission = notificationManager.getNotificationPermission();
      setBrowserPermission(permission);
    } catch (error) {
      console.error("Failed to get notification permission:", error);
      setBrowserPermission("unsupported");
    }
  }, []);

  useEffect(() => {
    // Initial permission check
    updatePermission();

    // Listen for visibility changes to update permission when user returns to tab
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updatePermission();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Also check when window gains focus
    window.addEventListener("focus", updatePermission);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", updatePermission);
    };
  }, [updatePermission]);

  const handleRequestPermission = async () => {
    if (browserPermission === "unsupported") return;

    setIsRequesting(true);
    try {
      const permission = await Notification.requestPermission();
      setBrowserPermission(permission);

      // Double-check with our manager after permission change
      setTimeout(() => {
        updatePermission();
      }, 100);
    } catch (error) {
      console.error("Failed to request notification permission:", error);
    } finally {
      setIsRequesting(false);
    }
  };

  const testNotification = () => {
    if (browserPermission === "granted") {
      try {
        new Notification(t("profile.testNotification.title"), {
          body: t("profile.testNotification.body"),
        });
      } catch (error) {
        console.error("Failed to create test notification:", error);
      }
    }
  };

  const getPermissionStatus = () => {
    switch (browserPermission) {
      case "granted":
        return {
          icon: <CheckCircle className="h-3 w-3" />,
          label: t("profile.browserNotificationsStatus.enabled"),
          description: t("profile.browserNotificationsDescription.enabled"),
          badgeClassName:
            "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700",
        };
      case "denied":
        return {
          icon: <BellOff className="h-3 w-3" />,
          label: t("profile.browserNotificationsStatus.blocked"),
          description: t("profile.browserNotificationsDescription.blocked"),
          badgeClassName:
            "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700",
        };
      case "unsupported":
        return {
          icon: <AlertTriangle className="h-3 w-3" />,
          label: t("profile.browserNotificationsStatus.unsupported"),
          description: t("profile.browserNotificationsDescription.unsupported"),
          badgeClassName:
            "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600",
        };
      default:
        return {
          icon: <Bell className="h-3 w-3" />,
          label: t("profile.browserNotificationsStatus.notConfigured"),
          description: t("profile.browserNotificationsDescription.notConfigured"),
          badgeClassName:
            "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-orange-300 dark:border-orange-700",
        };
    }
  };

  const permissionStatus = getPermissionStatus();

  return (
    <div className={className}>
      {/* Browser Notifications */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <Monitor className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{t("profile.browserNotifications")}</span>
              <Badge className={permissionStatus.badgeClassName}>
                {permissionStatus.icon}
                <span className="ml-1">{permissionStatus.label}</span>
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{permissionStatus.description}</p>
          </div>

          <div className="flex items-center gap-2">
            {browserPermission === "granted" && (
              <Button variant="outline" size="sm" onClick={testNotification}>
                {t("profile.browserNotificationsActions.test")}
              </Button>
            )}

            {browserPermission === "default" && (
              <Button size="sm" onClick={handleRequestPermission} disabled={isRequesting}>
                {isRequesting
                  ? t("profile.browserNotificationsActions.requesting")
                  : t("profile.browserNotificationsActions.enable")}
              </Button>
            )}
          </div>
        </div>

        {browserPermission === "denied" && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{t("profile.browserNotificationsAlert")}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}
